import { useThreadStore } from "@/store";
import { useCallback } from "react";
import { detectNeedsAnalysis, processFilesForMessage } from "../utils";
import { extractToolData } from "@/components/Chat/messages/utils/toolDataExtractors";

// Request deduplication for message submission
const processedRequests = new Map();
const REQUEST_EXPIRY = 30000; // 30 seconds

// Clear old requests periodically
setInterval(() => {
  const now = Date.now();
  for (const [key, timestamp] of processedRequests.entries()) {
    if (now - timestamp > REQUEST_EXPIRY) {
      processedRequests.delete(key);
    }
  }
}, 120000); // Clean up every 2 minutes

export const useMessageSubmission = (
  message,
  setMessage,
  files,
  setFiles,
  storageFiles,
  setStorageFiles,
  referencedItems,
  clearReferencedItems,
  isUploading,
  disabled,
  currentThreadId,
  isDeepResearchEnabled,
  onSendMessage,
  fileStorageStore,
  replyToMessage,
  cancelReplyToMessage
) => {
  const isLoading = useThreadStore((state) => state.isLoading);
  const isStreaming = useThreadStore((state) => state.isStreaming);

  const handleSubmit = useCallback(
    async (e, additionalContextData = {}) => {
      e?.preventDefault();

      if (isUploading) {
        return;
      }

      // CRITICAL FIX: Prevent duplicate submissions while streaming or loading
      if (isLoading || isStreaming) {
        console.warn(
          "[useMessageSubmission] Rejected submission - already loading/streaming"
        );
        return;
      }

      if ((!message.trim() && files.length === 0) || isUploading || disabled)
        return;

      const requestSignature = JSON.stringify({
        message: message.trim(),
        fileCount: files.length,
        fileIds: files.map((f) => f.id).join(","),
        timestamp: Math.floor(Date.now() / 5000),
      });

      if (processedRequests.has(requestSignature)) {
        return;
      }

      processedRequests.set(requestSignature, Date.now());

      try {
        const messageFiles = files
          .filter((f) => f.openai_file_id && f.status === "uploaded")
          .map((f) => ({
            file_id: f.openai_file_id,
            type: f.type.startsWith("image/") ? "image_file" : "file",
            purpose: f.purpose || "assistants",
          }));

        let finalMessage = message.trim();

        // Automatic canvas context detection
        // Check if we should automatically include canvas context even without explicit reply
        let autoCanvasContext = null;

        // Keywords that suggest iterative canvas editing
        const iterativeKeywords = [
          "add",
          "also",
          "update",
          "change",
          "modify",
          "edit",
          "include",
          "remove",
          "delete",
          "replace",
          "now",
          "then",
          "next",
          "make it",
          "adjust",
          "improve",
          "enhance",
          "extend",
          "expand",
          "reduce",
          "fix",
          "correct",
          "refactor",
          "rewrite",
        ];

        const messageWords = finalMessage.toLowerCase().split(/\s+/);
        const hasIterativeKeyword = iterativeKeywords.some((keyword) =>
          messageWords.some((word) => word.startsWith(keyword))
        );

        // Only auto-detect if not already replying explicitly
        if (!replyToMessage?.id && hasIterativeKeyword) {
          const allMessages = useThreadStore.getState().messages;

          // Find the most recent assistant message with a canvas
          for (let i = allMessages.length - 1; i >= 0; i--) {
            const msg = allMessages[i];
            if (msg?.role === "assistant") {
              const toolData = extractToolData(msg, false);
              if (toolData?.canvasData?.content) {
                autoCanvasContext = {
                  messageId: msg.id,
                  role: msg.role,
                  content: msg.content || "",
                  canvasData: {
                    content: toolData.canvasData.content || "",
                    type:
                      toolData.canvasData.type ||
                      toolData.canvasData.contentType ||
                      "text",
                    language: toolData.canvasData.language || "javascript",
                    name: toolData.canvasData.name,
                  },
                };
                console.log("🎨 Auto-detected canvas context:", {
                  hasIterativeKeyword,
                  messageId: msg.id,
                  canvasContentLength: toolData.canvasData.content?.length,
                  detectedKeywords: messageWords.filter((word) =>
                    iterativeKeywords.some((kw) => word.startsWith(kw))
                  ),
                });
                break;
              }
            }
          }
        }

        // Store reply context for future backend use (not in visible message content)
        let replyContextData = null;
        if (replyToMessage?.id) {
          const allMessages = useThreadStore.getState().messages;
          const referencedMessage = allMessages.find(
            (msg) => msg.id === replyToMessage.id
          );

          if (referencedMessage) {
            // Extract canvas data if the referenced message has it
            const toolData = extractToolData(referencedMessage, false);
            const canvasData = toolData?.canvasData;

            console.log("🎨 Reply to message - toolData:", toolData);
            console.log("🎨 Reply to message - canvasData:", canvasData);

            replyContextData = {
              messageId: referencedMessage.id,
              role: referencedMessage.role,
              content: referencedMessage.content || "",
              preview: referencedMessage.content?.substring(0, 500) || "",
              // Include canvas data if present
              ...(canvasData && {
                canvasData: {
                  content: canvasData.content || "",
                  type: canvasData.type || canvasData.contentType || "text",
                  language: canvasData.language || "javascript",
                  name: canvasData.name,
                },
              }),
            };

            console.log("🎨 Reply context data:", replyContextData);
          }
        }

        // If replying to a message with canvas, prepend canvas context to help AI understand
        if (replyToMessage?.id && replyContextData?.canvasData) {
          console.log("🎨 Adding canvas context from explicit reply!");
          const canvasContext = `[Context: This is a reply to a canvas with the following content:\n\`\`\`${replyContextData.canvasData.language || "text"}\n${replyContextData.canvasData.content}\n\`\`\`]\n\n`;
          finalMessage = `${canvasContext}${finalMessage}`;
          console.log(
            "🎨 Final message with canvas context:",
            finalMessage.substring(0, 500)
          );
        }
        // If auto-detected canvas context (iterative editing), prepend it
        else if (autoCanvasContext?.canvasData) {
          console.log("🎨 Adding auto-detected canvas context!");
          const canvasContext = `[Context: Continuing from previous canvas with the following content:\n\`\`\`${autoCanvasContext.canvasData.language || "text"}\n${autoCanvasContext.canvasData.content}\n\`\`\`]\n\n`;
          finalMessage = `${canvasContext}${finalMessage}`;
          console.log(
            "🎨 Final message with auto canvas context:",
            finalMessage.substring(0, 500)
          );

          // Set replyContextData so metadata is stored
          replyContextData = autoCanvasContext;
        }

        if (referencedItems.length > 0) {
          const referencedText = referencedItems
            .map((item) => `@${item.displayName}`)
            .join(" ");

          if (finalMessage) {
            finalMessage = `${referencedText} ${finalMessage}`;
          } else {
            finalMessage = referencedText;
          }
        }

        const enhancedStorageFiles = storageFiles.map((file) => {
          const isImageForVision =
            file.isImage || file.type?.startsWith("image/");

          const isPdf =
            file.isPdf ||
            file.type === "application/pdf" ||
            file.name?.toLowerCase().endsWith(".pdf");

          return {
            ...file,
            thread_id: currentThreadId,
            metadata: {
              ...(file.metadata || {}),
              thread_id: currentThreadId,
              openai_thread_id: currentThreadId,
              supabase_thread_id: currentThreadId,
              purpose: file.purpose || "assistants",
              isImage: isImageForVision,
              isPdf: isPdf,
              isDocument:
                file.isDocument ||
                file.type?.includes("pdf") ||
                file.type?.includes("document") ||
                file.type?.includes("text/") ||
                false,
              requiresFunctionCall: true,
            },
            visionMetadata: isImageForVision
              ? {
                  publicUrl:
                    file.visionMetadata?.publicUrl ||
                    file.data?.publicUrl ||
                    file.publicUrl ||
                    file.url,
                  isReady: true,
                  contentType: file.type || file.data?.type,
                }
              : null,
          };
        });

        // Update thread context with files
        try {
          if (currentThreadId && enhancedStorageFiles.length > 0) {
            const { getThreadContext, setThreadContext } =
              useThreadStore.getState();
            const currentContext = getThreadContext(currentThreadId);

            if (currentContext) {
              const fileRefs = enhancedStorageFiles.map((file) => ({
                id: file.id || file.data?.id || `file-${Date.now()}`,
                path: file.data?.path || file.path || file.name,
                name: file.name || file.data?.name,
                type: file.type || file.data?.type,
                size: file.size || file.data?.size,
                thread_id: currentThreadId,
                url: file.data?.publicUrl || file.publicUrl,
                openai_file_id: file.openai_file_id,
                purpose: file.purpose || "assistants",
                isImage:
                  file.isImage || file.type?.startsWith("image/") || false,
                isPdf:
                  file.isPdf ||
                  file.type === "application/pdf" ||
                  file.name?.toLowerCase().endsWith(".pdf"),
                isDocument:
                  file.isDocument ||
                  file.type?.includes("pdf") ||
                  file.type?.includes("document") ||
                  file.type?.includes("text/") ||
                  false,
                needsAnalysis: file.needsAnalysis || detectNeedsAnalysis(file),
                needsVision:
                  file.isImage || file.type?.startsWith("image/") || false,
                needsFileSearch:
                  file.isPdf ||
                  file.isDocument ||
                  file.type?.includes("pdf") ||
                  file.type?.includes("document"),
                created_at: new Date().toISOString(),
                requiresFunctionCall: true,
                visionMetadata:
                  file.isImage || file.type?.startsWith("image/")
                    ? {
                        publicUrl: file.data?.publicUrl || file.publicUrl,
                        isReady: true,
                        contentType: file.type || file.data?.type,
                      }
                    : null,
                embeddingJobId: file.embeddingJobId,
                embeddingsProcessed: file.embeddingsProcessed || false,
              }));

              setThreadContext({
                ...currentContext,
                storage_files: [
                  ...(currentContext.storage_files || []),
                  ...fileRefs,
                ],
              });
            }
          }
        } catch (err) {
          console.warn("Failed to update thread context with files:", err);
        }

        const embeddingFiles =
          storageFiles?.filter(
            (file) =>
              detectNeedsAnalysis(file) ||
              file.needsAnalysis === true ||
              file.needsEmbeddings === true ||
              file.embeddingsProcessed === true
          ) || [];

        const embeddingFileIds = embeddingFiles
          .filter(
            (file) => file.openai_file_id || file.file_id || file.embeddingJobId
          )
          .map((file) => {
            if (file.embeddingJobId) {
              return `emb_${file.embeddingJobId}__${file.openai_file_id || file.file_id}`;
            }
            return file.openai_file_id || file.file_id;
          })
          .filter(Boolean);

        const hasEmbeddingFiles = embeddingFiles.length > 0;

        const imageCount = files.filter(
          (file) => file.isImage || file.type?.startsWith("image/")
        ).length;
        const pdfCount = files.filter(
          (file) =>
            file.isPdf ||
            file.type === "application/pdf" ||
            file.name?.toLowerCase().endsWith(".pdf")
        ).length;
        const documentCount = files.filter(
          (file) =>
            !file.isImage &&
            !file.isPdf &&
            file.type !== "application/pdf" &&
            !file.name?.toLowerCase().endsWith(".pdf") &&
            (file.isDocument ||
              file.type?.includes("document") ||
              file.type?.includes("text/"))
        ).length;
        const analysisCount = files.filter((file) =>
          detectNeedsAnalysis(file)
        ).length;
        const otherFileCount =
          files.length -
          (imageCount + pdfCount + documentCount + analysisCount);

        const processedFileMetadata = processFilesForMessage(
          storageFiles || files
        );

        // Deduplicate files between files and storageFiles arrays
        // This prevents Google Drive files from appearing twice in message attachments
        const deduplicatedStorageFiles = enhancedStorageFiles.filter(
          (storageFile) => {
            // Check if this storage file is already represented in the files array
            const isDuplicateInFiles = files.some((file) => {
              // Compare by multiple criteria to catch duplicates
              return (
                // Same OpenAI file ID
                (storageFile.openai_file_id &&
                  file.openai_file_id &&
                  storageFile.openai_file_id === file.openai_file_id) ||
                // Same Google Drive ID
                (storageFile.isGoogleDrive &&
                  file.isGoogleDrive &&
                  storageFile.id === file.id) ||
                // Same file name and size (for local files)
                (storageFile.name === file.name &&
                  storageFile.size === file.size &&
                  storageFile.type === file.type)
              );
            });

            // Only include storage files that are NOT duplicated in files array
            return !isDuplicateInFiles;
          }
        );

        // Combine files and deduplicated storage files for metadata
        const allUniqueFiles = [...files, ...deduplicatedStorageFiles];

        // Build visionImageUrls directly from allUniqueFiles which has complete file data
        // This fixes the bug where storageFiles had URLs nested at file.data.publicUrl
        // but the old logic only checked file.publicUrl || file.url
        const visionImageUrls = allUniqueFiles
          .filter((file) => file.isImage || file.type?.startsWith("image/"))
          .map(
            (file) =>
              file.publicUrl ||
              file.url ||
              file.data?.publicUrl ||
              file.visionMetadata?.publicUrl ||
              file.localUrl
          )
          .filter(Boolean);

        const pdfFileIds = allUniqueFiles
          .filter(
            (file) =>
              (file.isPdf ||
                file.type === "application/pdf" ||
                file.name?.toLowerCase().endsWith(".pdf")) &&
              (file.openai_file_id || file.file_id)
          )
          .map((file) => file.openai_file_id || file.file_id)
          .filter(Boolean);

        const analysisFileIds = allUniqueFiles
          .filter(
            (file) =>
              detectNeedsAnalysis(file) && (file.openai_file_id || file.file_id)
          )
          .map((file) => file.openai_file_id || file.file_id)
          .filter(Boolean);

        const messageMetadata = {
          // Essential file detection flags - use deduplicated count
          has_files: allUniqueFiles.length > 0,
          file_count: allUniqueFiles.length,

          // Vision-related (actually used in streamProviderChat and providers)
          has_images:
            allUniqueFiles.filter(
              (f) => f.isImage || f.type?.startsWith("image/")
            ).length > 0,
          image_count: allUniqueFiles.filter(
            (f) => f.isImage || f.type?.startsWith("image/")
          ).length,
          vision_image_urls: visionImageUrls,

          // PDF processing (used in pdfProcessing.js)
          has_pdfs: pdfFileIds.length > 0,
          pdf_file_ids: pdfFileIds,

          // Vector store and retrieval (essential for RAG functionality)
          enable_retrieval:
            hasEmbeddingFiles ||
            pdfFileIds.length > 0 ||
            allUniqueFiles.filter(
              (f) =>
                f.isDocument ||
                f.type?.includes("document") ||
                f.type?.includes("text/")
            ).length > 0,
          // SIMPLIFIED: Vector store ID will be retrieved from thread store when needed

          // Deep research flag
          enable_deep_research: isDeepResearchEnabled,

          // Reply to message
          ...(replyToMessage?.id && { reply_to: replyToMessage.id }),

          // Reply context for backend to construct proper prompt
          ...(replyContextData && { reply_context: replyContextData }),

          // Storage files data (for file processing) - use only deduplicated storage files
          // Enhanced to check all possible URL locations and include visionMetadata
          storage_files:
            deduplicatedStorageFiles.length > 0
              ? JSON.stringify(
                  deduplicatedStorageFiles.map((file) => {
                    const isImage =
                      file.isImage || file.type?.startsWith("image/");
                    const resolvedUrl =
                      file.publicUrl ||
                      file.url ||
                      file.data?.publicUrl ||
                      file.visionMetadata?.publicUrl;
                    return {
                      id: file.id || file.openai_file_id,
                      name: file.name,
                      type: file.type,
                      path: file.path || file.storage_path || file.data?.path,
                      openai_file_id: file.openai_file_id,
                      isImage: isImage,
                      isPdf: file.isPdf,
                      publicUrl: resolvedUrl,
                      visionMetadata: isImage
                        ? { publicUrl: resolvedUrl }
                        : null,
                    };
                  })
                )
              : null,
        };

        // For messages with images but no URLs, add blob URLs directly
        if (imageCount > 0 && visionImageUrls.length === 0) {
          console.warn(
            "Images detected but no URLs found, attempting to use blob URLs"
          );
          const blobUrls = files
            .filter((file) => file.isImage || file.type?.startsWith("image/"))
            .map(
              (file) =>
                file.localUrl ||
                file.visionMetadata?.publicUrl ||
                file.publicUrl ||
                file.url
            )
            .filter(Boolean);

          if (blobUrls.length > 0) {
            messageMetadata.vision_image_urls = blobUrls;
          }
        }

        // CRITICAL: Don't include workspace-related flags for regular chats
        const isWorkspaceChat =
          localStorage.getItem("pendingWorkspaceChat") !== null;

        onSendMessage({
          content: finalMessage,
          files: messageFiles,
          storageFiles: enhancedStorageFiles,
          fileMetadata: files.map(
            ({
              id,
              name,
              type,
              size,
              openai_file_id,
              purpose,
              isImage,
              isPdf,
              isDocument,
              publicUrl,
              data,
              visionMetadata,
              url,
              path,
              file_id,
              localUrl,
              needsAnalysis,
              needsEmbeddings,
              embeddingJobId,
              embeddingsProcessed,
            }) => ({
              id,
              name,
              type,
              size,
              openai_file_id,
              file_id: openai_file_id || file_id || id,
              thread_id: currentThreadId,
              purpose: purpose || "assistants",
              isImage: isImage || type?.startsWith("image/") || false,
              isPdf:
                isPdf ||
                type === "application/pdf" ||
                name?.toLowerCase().endsWith(".pdf"),
              isDocument:
                isDocument ||
                type?.includes("pdf") ||
                type?.includes("document") ||
                type?.includes("text/") ||
                false,
              requiresFunctionCall: true,
              needsVision: isImage || type?.startsWith("image/") || false,
              needsFileSearch: isPdf || isDocument,
              needsAnalysis:
                needsAnalysis || detectNeedsAnalysis({ type, name }) || false,
              needsEmbeddings:
                needsEmbeddings ||
                needsAnalysis ||
                detectNeedsAnalysis({ type, name }) ||
                false,
              embeddingJobId,
              embeddingsProcessed: embeddingsProcessed || false,
              visionMetadata:
                isImage || type?.startsWith("image/")
                  ? visionMetadata || {
                      publicUrl:
                        data?.publicUrl || publicUrl || url || localUrl,
                      contentType: type,
                      isReady: true,
                      path: path || data?.path,
                    }
                  : null,
            })
          ),
          contextData: {
            ...messageMetadata,
            // Only include workspace flags if this is actually a workspace chat
            ...(isWorkspaceChat
              ? {
                  from_workspace_chat: true,
                  workspace_initiated: true,
                  is_workspace_chat: true,
                }
              : {}),
            // Include any additional context data (like force tool flags)
            ...additionalContextData,
          },
          referencedItems: referencedItems.map((item) => ({
            id: item.id,
            type: item.displayType,
            name: item.displayName,
            thread_id: currentThreadId,
          })),
          metadata: messageMetadata,
          isDeepResearch: isDeepResearchEnabled,
          enableDeepResearch: isDeepResearchEnabled,
        });

        // Reset the input state
        setMessage("");
        setFiles([]);
        clearReferencedItems();

        // Clear reply context
        if (replyToMessage && cancelReplyToMessage) {
          cancelReplyToMessage();
        }

        // Clear storageFiles after sending the message
        setStorageFiles([]);

        // Clear the currentFiles in fileStorageStore
        fileStorageStore.clearCurrentFiles();
      } catch (error) {
        console.error("Error submitting message:", error);
        throw error;
      }
    },
    [
      isUploading,
      isLoading,
      isStreaming,
      message,
      files,
      disabled,
      referencedItems,
      storageFiles,
      currentThreadId,
      isDeepResearchEnabled,
      onSendMessage,
      setMessage,
      setFiles,
      clearReferencedItems,
      setStorageFiles,
      fileStorageStore,
      replyToMessage,
      cancelReplyToMessage,
    ]
  );

  return {
    handleSubmit,
  };
};
